/**
 * AI connection — manages which AI clients are authorized to act as
 * the signed-in user on Kin. Used to be the heavy onboarding page
 * with skill + MCP install steps for every client; that content now
 * lives in /docs/getting-started. This page is for: (a) verifying
 * the connection works (the ConnectionStatus banner reads live
 * OAuth grant state), and (b) revoking grants for stale clients.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api/client.js';
import type { OAuthGrantSummary } from '../api/types.js';
import {
  IconCheck,
  IconInfo,
  IconPlug,
  IconRocket,
  IconSparkle,
  IconTrash,
} from '../components/Icons.js';
import {
  Banner,
  Button,
  ButtonLink,
  Card,
  CardHeader,
  EmptyState,
  PageHeader,
  PageSkeleton,
} from '../components/ui.js';
import { relTime } from '../lib/format.js';

interface GrantsState {
  loading: boolean;
  grants?: OAuthGrantSummary[];
  error?: string;
}

export function Setup() {
  const [grantsState, setGrantsState] = useState<GrantsState>({ loading: true });
  const [revoking, setRevoking] = useState<string | null>(null);

  const loadGrants = useCallback(async () => {
    setGrantsState((prev) =>
      prev.grants ? { loading: true, grants: prev.grants } : { loading: true },
    );
    try {
      const res = await api.get<{ grants: OAuthGrantSummary[] }>('/api/mcp/grants');
      setGrantsState({ loading: false, grants: res.grants });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : String(err);
      setGrantsState({ loading: false, error: message });
    }
  }, []);

  useEffect(() => {
    void loadGrants();
  }, [loadGrants]);

  async function revoke(id: string) {
    if (!confirm('Disconnect this client? It will need to reauthorize before its next call.')) {
      return;
    }
    setRevoking(id);
    try {
      await api.delete(`/api/mcp/grants/${encodeURIComponent(id)}`);
      await loadGrants();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : String(err);
      setGrantsState((prev) => ({ ...prev, loading: false, error: message }));
    } finally {
      setRevoking(null);
    }
  }

  const grants = grantsState.grants ?? [];
  const connected = grants.length > 0;

  return (
    <div className="animate-fade-in">
      <PageHeader
        eyebrow="Account"
        title="AI connection"
        description="Authorized AI clients that can act as you on Kin. Disconnect any you no longer use."
      />

      <Card className="!bg-brand-soft/30 !border-brand/20 mb-6">
        <div className="px-5 py-5 flex gap-4 items-start">
          <div className="flex-none w-10 h-10 rounded-xl bg-brand/10 text-brand flex items-center justify-center">
            <IconSparkle size={20} />
          </div>
          <div className="min-w-0 text-sm space-y-2">
            <p className="font-display text-base font-semibold tracking-tight text-ink">
              Most people don&apos;t need anything here
            </p>
            <p className="text-ink-2">
              The fastest way to build is the <strong className="text-ink">in-browser chat</strong>{' '}
              &mdash; create an app from{' '}
              <a className="underline" href="/apps/new">
                Apps &rarr; Start building
              </a>{' '}
              and the Kin chat bubble appears next to your preview. Zero install, no OAuth grant
              needed.
            </p>
            <p className="text-muted">
              This page only matters for the <strong className="text-ink">Claude Code</strong> path
              (or other MCP clients) &mdash; each authorized client shows up below. Install the
              plugin with <span className="font-mono text-xs">/plugin install kin@mytra</span>; the
              first tool call mints the grant.
            </p>
            <div className="flex gap-2 pt-1">
              <ButtonLink to="/apps/new" variant="primary" size="sm">
                <IconRocket size={13} />
                Create an app
              </ButtonLink>
              <ButtonLink to="/docs/getting-started" variant="secondary" size="sm">
                Setup walkthrough
              </ButtonLink>
            </div>
          </div>
        </div>
      </Card>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold tracking-tight inline-flex items-center gap-2">
            <IconPlug size={16} className="text-muted" />
            Connected clients
          </h2>
          <Button variant="ghost" size="sm" onClick={() => void loadGrants()}>
            Refresh
          </Button>
        </div>

        {grantsState.error && <Banner tone="error">{grantsState.error}</Banner>}

        {grantsState.loading && !grantsState.grants && <PageSkeleton rows={2} />}

        {!grantsState.loading && !connected && !grantsState.error && (
          <EmptyState
            icon={<IconInfo size={18} />}
            title="No AI client has signed in yet"
            description="Open your AI agent and ask it to list your Kin apps. The first request triggers a one-time sign-in; after that, the client shows up here."
            action={
              <ButtonLink to="/docs/getting-started" variant="secondary" size="sm">
                See verification steps
              </ButtonLink>
            }
          />
        )}

        {connected && (
          <Card>
            <CardHeader
              title={
                <span className="inline-flex items-center gap-2">
                  <IconCheck size={14} className="text-ok" />
                  {grants.length} connected client{grants.length === 1 ? '' : 's'}
                </span>
              }
              description="Each row is one AI client authorized to call Kin tools as you."
            />
            <ul className="divide-y divide-line">
              {grants.map((g) => (
                <li
                  key={g.id}
                  className="flex items-center justify-between gap-4 px-5 py-3 text-sm"
                >
                  <div className="min-w-0">
                    <div className="font-medium text-ink truncate">
                      {g.clientName ?? g.clientId}
                    </div>
                    <div className="text-xs text-muted mt-0.5">
                      Connected {relTime(g.createdAt)}
                      {g.expiresAt != null && ` · expires ${relTime(g.expiresAt)}`}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => void revoke(g.id)}
                    loading={revoking === g.id}
                    disabled={revoking === g.id}
                    className="!text-danger hover:!bg-danger-soft"
                  >
                    <IconTrash size={13} />
                    Disconnect
                  </Button>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </section>
    </div>
  );
}
